import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, filter, map, merge, combineLatest, take } from 'rxjs';

import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { Coaching } from '@app/shared/classes/coaching';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { AppDataState } from '@app/shared/stores/app-data.state';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { DataGridColumnDef, GetCoachingSearchByList } from './coaching.constant';
import { CoachingDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/coaching-data-grid-cell.component';
import { CoachingEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/coaching-edit-modal-dialog/coaching-edit-modal-dialog.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-coaching',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule, DisableEditButtonDirective,
    SmartSearchFormComponent, SpinnerButtonComponent, ImportDataResultComponent, CoachingDataGridCell],
  templateUrl: './coaching.component.html',
  styleUrl: './coaching.component.css'
})
export class CoachingComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.COACHING_SEARCH_FORM;
  selectedItems: Coaching[] = [];

  dataSource$: Observable<Coaching[]>;
  CoachingSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentCoachingSearchBy) currentCoachingSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_COACHING)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_COACHING)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_COACHING)) response$: Observable<WebApiResponse<Coaching[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_COACHING)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_COACHING;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentCoachingSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.CoachingSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map((c) => GetCoachingSearchByList(c)));
  }

  rowSelected = (data: Coaching[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetCoaching());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetCoaching(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_COACHING;
    this.store.dispatch(new AppDataAction.GetCoaching(info));
  }

  onCreate = () => {
    this.dialog.open(CoachingEditModalDialogComponent, {
      width: '700px',
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true
    });
  }

  onEdit = () => {
    this.dialog.open(CoachingEditModalDialogComponent, {
      width: '700px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }
}
